import type { GetServerSideProps } from 'next';
import { getServerSession } from 'next-auth';
import axios, { type AxiosResponse } from 'axios';
import { useEffect } from 'react';
import Head from 'next/head';
import getConfig from 'next/config';
import { NextSeo } from 'next-seo';

import { VerificationProvider } from '../contexts/VerificationContext';
import { DefaultLayout } from '../components/Layouts';
import { adViewApi } from '../api/adViewApi';

import type { HomePageResponse } from '../api/types/adviewApiTypes';
import { HomePage } from '../components/HomePage/HomePage';
import { Notifications } from '../types';
import { logger } from '@dist-marketplace-frontend/services';
import { PAGE } from '../helpers/pages';
import { useUserContext } from '../contexts/UserContext';
import { nextAuthOptions } from '../helpers/nextAuthOptions';
import { HomePageProvider } from '../contexts/HomePageContext';
import { searchPageApi } from '../api/searchPageApi';
import type { ISearchPageFilter } from '../api/types/searchPageApiTypes';
import MetricService from '../services/MetricService';
import { getVerticalServer, VERTICAL } from '../helpers/verticals';
import {
  HomePageData,
  transformHomePageResponse,
} from '../components/HomePage/helpers';
import { useImproveRecaptchaEfficiency } from '../hooks/UseImproveRecaptchaEfficiency';
import { getUserProfile } from '../services/userProfileService';
import { UserProfile } from '../api/schemas/user-profile.schema';
import { getRecommendationId } from '../helpers/getRecommendationId';

const {
  publicRuntimeConfig: { CDN_STATIC_ASSETS, DFP_NETWORK_ID },
} = getConfig();

interface ServerSideProps {
  data: HomePageData;
  domain?: string;
  notificationData?: Notifications;
  unreadInAppMessage?: string;
  updatedUser: UserProfile | null;
  motorSearchFilters: ISearchPageFilter[];
}

function Home(props: ServerSideProps) {
  const { dispatchUserProfile } = useUserContext();

  useImproveRecaptchaEfficiency('home_page');

  const {
    domain = '',
    notificationData,
    unreadInAppMessage,
    updatedUser,
    motorSearchFilters,
  } = props;

  useEffect(() => {
    if (updatedUser) {
      dispatchUserProfile({
        type: 'UPDATE',
        payload: updatedUser,
      });
    }
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedUser]);

  return (
    <HomePageProvider>
      <VerificationProvider>
        <NextSeo canonical={domain} />
        <Head>
          <meta
            name="google-site-verification"
            content="GHUykIchXG6tmbMfI3F6rSG5MVDBjzJN6iCyRilt8Kw"
          />
        </Head>

        <DefaultLayout
          domain={domain}
          headerVariant="DEFAULT"
          notificationData={notificationData}
          unreadInAppMessage={unreadInAppMessage}
        >
          <HomePage
            init={props.data}
            domain={domain}
            dfpNetworkId={DFP_NETWORK_ID}
            cdnUrl={CDN_STATIC_ASSETS}
            unreadInAppMessage={props.unreadInAppMessage}
            motorSearchFilters={motorSearchFilters}
          />
        </DefaultLayout>
      </VerificationProvider>
    </HomePageProvider>
  );
}

export const getServerSideProps: GetServerSideProps<ServerSideProps> = async (
  ctx,
) => {
  const { query } = ctx;
  const { headers } = ctx.req;
  const { cookie } = headers;

  let motorSearchFilters: ISearchPageFilter[] = [];
  try {
    const { data } = await searchPageApi.getCarsFiltersWithCount();
    motorSearchFilters = data.showByDefault || [];
  } catch (error) {
    logger.error({
      route: PAGE.HOME,
      error,
      message: 'Error retrieving homepage motor filters',
    });
  }

  try {
    let notificationData;
    let unreadInAppMessage;

    let updatedUser: UserProfile | null = null;

    /**
     * The following code is to support this flow:
     * 1. A new user signs up with keycloak
     * 2. As they have just signed up, they are not verified
     * 3. They can't do certain actions until they verify
     * 4. They open email to verify, follow link and redirect to site
     * 5. Although keycloak knows they are verified, the legacy DDUS cookie is now outdated.
     *
     * So we get the up to date user information and update the user state
     *
     */
    const session = await getServerSession(ctx.req, ctx.res, nextAuthOptions);
    if (session?.user) {
      updatedUser = await getUserProfile(session.accessToken);
    }

    const cookieVertical = getVerticalServer(ctx);

    const recommendationId = getRecommendationId(ctx, session?.user);
    const getHomePageDataRequestHeaders = {
      ...(recommendationId && {
        dd_recommendation_user: recommendationId,
      }),
      ...(cookie && { cookie }),
      'x-forwarded-for': headers['cf-connecting-ip'],
    };

    const {
      data,
      headers: { unreadhistorychecks, unreadmessages, totalunread, unreadiam },
    }: AxiosResponse<HomePageResponse> = await adViewApi.getHomePageData(
      cookieVertical,
      getHomePageDataRequestHeaders,
      query,
    );

    notificationData = {
      totalUnread: totalunread ?? null,
      unreadHistoryChecks: unreadhistorychecks ?? null,
      unreadMessages: unreadmessages ?? null,
    };

    unreadInAppMessage = unreadiam ?? null;

    return {
      props: {
        data: transformHomePageResponse(data),
        notificationData,
        unreadInAppMessage,
        type: 'success',
        updatedUser,
        motorSearchFilters,
      },
    };
  } catch (error) {
    logger.error({
      route: PAGE.HOME,
      error,
      message:
        'Error getting server session OR getting user OR getting home page data',
    });
    // TODO: separate into separate try catch blocks

    if (axios.isAxiosError(error)) {
      const metricService = MetricService.get();
      metricService.incrementRequestErrorCount('HOME', {
        statusCode: error.response?.status,
      });
    }

    // if error we pass empty props so homepage is still rendered
    const data: HomePageData = {
      sectionsTitle: '',
      featuredAds: null,
      dataTargetingProps: {
        gtm: {},
        dfp: {},
      },
      recentAds: [],
      vertical: VERTICAL.MOTOR,
      recentAdsUrl: null,
      seoLinks: { internalLinks: [] },
      sections: [],
      dashLinks: [],
      lifestyleLinks: [],
      adCount: {
        carsTotalAdCount: 0,
      },
    };

    return {
      props: {
        data,
        type: 'error',
        updatedUser: null,
        motorSearchFilters,
      },
    };
  }
};

export default Home;
